/*
FormSelect is a drop down selector. Usage:

            <FormSelect
              v-model="parent id value"
              :label="field label"
              fieldConfig="field configuration properties"
            />
*/
import Configuration from '@/agilis/util/Configuration'
import Vue from 'vue'
import { mapActions, mapGetters } from 'vuex'
import L from 'lodash'
import validationMixin from '../utils/validationMixin'
import TrackerDataTableItemDialogEdit from '@/applications/scrum/components/TrackerDataTable/TrackerDataTableItemDialogEdit/TrackerDataTableItemDialogEdit.vue'

export default {
  name: 'FormSelect',
  mixins: [validationMixin],

  components: {
    TrackerDataTableItemDialogEdit
  },

  props: {
    label: String,
    hint: String,
    value: [ Object, String, Number, Boolean, Array ],
    fieldConfig: Object,
    trackerDTO: Object,
    required: Boolean,
    menuProps: { type: Object, default: () => {} },
    multiple: { type: Boolean, default: false },
    disabled: Boolean,
    hideDetails: {
      type: [ String, Boolean ],
      default: false
    },
    clearable: {
      type: Boolean,
      default: false
    }
  },

  data: () => ({
    autompleteFilterFn: {
      equals: (item, queryText, itemText) => {
        return itemText.toLocaleLowerCase() === queryText.toLocaleLowerCase()
      },
      like: (item, queryText, itemText) => {
        return itemText.toLocaleLowerCase().indexOf(queryText.toLocaleLowerCase()) > -1
      },
      startsWith: (item, queryText, itemText) => {
        return itemText.toLocaleLowerCase().indexOf(queryText.toLocaleLowerCase()) === 0
      }
    },
    items: [],
    _items: [], // unprocessed data
    query: null,

    showComboboxWorkaround: true, // workaround combobox: la label mostrata per l'opzione selezionata NON è item-text ma il valore
    openDialog: false,
    customItemDTO: {},
    apiFromConfig: '',
    internalSearch: ''
  }),

  created () {
    this.setConfigDefaults()
  },

  mounted: async function () {
    if (this.fieldConfig.options) {
      this.items = this.translateItems(this.fieldConfig.options, this.fieldConfig.itemText)
    } else if (this.fieldConfig.method) {
      const method = this.getMethod()
      method && method(this.fieldConfig, this.trackerDTO)
    } else {
      await this.fetchData()
    }

    let defaultValue = this.fieldConfig.defaultValue
    if (defaultValue === undefined && this.fieldConfig.initializeWithLoggedUser) {
      const loggedUser = this.items.find(i => i.userId === this.userId)
      defaultValue = loggedUser?.id
    }
    if (this.isEmpty && defaultValue !== undefined) {
      const defaultItem = this.items.find(i => i[this.fieldConfig.itemValue] === defaultValue)
      this.handleSelectChange(defaultItem)
    }

    // il defaultValue viene settato con il metodo getDTO di Configuration.js
    // qui viene eseguito l'override se esiste il filtro dbac per quel campo
    if (Configuration.auth.userHasDbacFilterPrivilege(this.selectedContext, this.fieldConfig.id)) {
      const dbac = Configuration.auth.userHasDbacFilterPrivilege(this.selectedContext, this.fieldConfig.id)
      const dbacItem = this.items.find(i => i[this.fieldConfig.itemValue] === dbac.substring(dbac.search('=') + 1))
      this.handleSelectChange(dbacItem)
    }

    if (!this.isEmpty && this.$refs.provider) {
      this.$refs.provider.validate()
    }
  },

  watch: {
    trackerDTO () {
      // force reload of select items when trackerDTO changes
      if (this.fieldConfig.method) {
        const method = this.getMethod()
        if (method) {
          method(this.fieldConfig, this.trackerDTO)
        }
      }
    },

    userLanguage () {
      // On language change, re-translate the options
      if (this.fieldConfig.options) {
        this.items = this.translateItems(this.fieldConfig.options, this.fieldConfig.itemText)
      }
    },

    async 'fieldConfig' (newValue, oldValue) {
      // Field config has changed: re-compute the default values
      this.setConfigDefaults()

      // When options are changes, re-translate
      if (this.fieldConfig.options && !L.isEqual(newValue.options, oldValue.options)) {
        this.items = this.translateItems(newValue.options, this.fieldConfig.itemText)
        if (this.value && !this.items.find(item => item[this.fieldConfig.itemValue] === (this.value || this.value[this.fieldConfig.itemValue]))) {
          this.handleSelectChange(null)
        }
      }

      // When url changes, re-load from API
      if (
        (this.fieldConfig.url && !L.isEqual(newValue.url, oldValue.url)) ||
        (this.fieldConfig.api && !L.isEqual(newValue.api, oldValue.api))
      ) {
        await this.fetchData()
      }

      if (this.fieldConfig.computed && this.isEmpty) {
        this.handleSelectChange(this.fieldConfig.computed)
      }
    }
  },

  computed: {
    ...mapGetters({
      applicationId: 'contextStore/applicationId',
      workspaceId: 'contextStore/workspaceId',
      selectedContext: 'contextStore/selectedContext',
      trackerTypeId: 'contextStore/trackerTypeId',
      trackerId: 'contextStore/trackerId',
      loading: 'loaderStore/loading',
      userLanguage: 'userDataStore/userLanguage',
      userId: 'userDataStore/userId'
    }),

    appendIcon () { // this replicates the behavior of the component in vuetify3 - no need to migrate this method
      if (this.items && this.items.length > 0) {
        return '$dropdown'
      } else {
        return null
      }
    },

    noDataText () {
      return this.fieldConfig.noDataText ? this.$t(this.fieldConfig.noDataText) : this.$t('label.noDataText')
    },

    isEmpty () {
      return this.value === undefined || (typeof this.value === 'object' && L.isEmpty(this.value))
    },

    comboboxSelectionWorkaround () { // workaround combobox: la label mostrata per l'opzione selezionata NON è item-text ma il valore
      if (this.value) {
        if (typeof this.value === 'object') return this.value[this.fieldConfig.itemText]

        const item = this.items.find(i => i[this.fieldConfig.itemValue] === this.value)
        return item ? item[this.fieldConfig.itemText] : this.value
      }
    }
  },

  methods: {
    ...mapActions({
      'notifyError': 'notificationStore/notifyError',
      'notifyMessage': 'notificationStore/notifyMessage',
      'startLoader': 'loaderStore/startLoader',
      'stopLoader': 'loaderStore/stopLoader'
    }),

    /**
     * It return the function implementing the method defined on fieldConfig for the items retrieval:
     * - method defiend as string method: "myMethod"
     * - method defined as Object { fn: "myMethod", params: { ... } }
     * @returns The implementation of the method defiend on fieldConfing
     */
    getMethod () {
      const method = this.fieldConfig.method
      if (this[method] && typeof this[method] === 'function') {
        return this[method]
      }
      console.warn(`FormSelect: invalid method "${method}" for field "${this.fieldConfig.id}"`)
      return null
    },
    userCanDoActionCreate (fieldConfig) {
      if (fieldConfig && fieldConfig.actionCreate) {
        const context = { ...this.selectedContext }
        context.trackerTypeId = fieldConfig.actionCreate?.trackerTypeId || context.trackerTypeId
        context.workspaceId = fieldConfig.actionCreate?.workspaceId || context.workspaceId
        context.applicationId = fieldConfig.actionCreate?.applicationId || context.applicationId
        return Configuration.auth.userCanDo(context, 'post')
      }
      return false
    },
    translateItems (items = [], textKey) {
      let _items = this.processItems(items)
      _items = _items.map((item) => {
        let textToTranslate = item
        if (typeof item === 'object') {
          textToTranslate = item[textKey]
        }
        const translatedText = this.$t(textToTranslate)
        return Object.assign({}, item, {
          [textKey]: translatedText
        })
      })

      const sortField = this.fieldConfig.sortField
      if (sortField && sortField === textKey) {
        _items.sort(this.propComparator(sortField))
      }

      return _items
    },

    handleSelectChange (_item = null) {
      let outItem = {} // map to expected output values
      let item = _item
      if (this.fieldConfig.combobox) {
        if (typeof item === 'string') { // map combobox input into a virtual item
          item = {
            [this.fieldConfig.itemText]: item.trim(),
            [this.fieldConfig.itemValue]: item.trim()
          }
        }

        this.showComboboxWorkaround = true
      }

      if (this.multiple) {
        outItem[this.fieldConfig.outValue] = item // TODO: implement the "transform flow" to decorate the model
      } else {
        if (item === null) {
          outItem[this.fieldConfig.outValue] = null
        } else {
          const transformJson = {}
          transformJson[this.fieldConfig.outValue] = this.fieldConfig.outValueExpr
          Configuration.datatable.jsonTransform(item, transformJson, outItem)
        }
        Configuration.datatable.propagateFieldInheritance(item, outItem, this.fieldConfig)
      }
      this.$emit('change', outItem)
      this.$nextTick(() => {
        this.internalSearch = ''
      })
    },

    onComboboxKeydown () { // workaround combobox: la label mostrata per l'opzione selezionata NON è item-text ma il valore
      this.showComboboxWorkaround = false
    },

    async fetchData () {
      const api = this.$axios.agilisComputeApiProp(this.fieldConfig)

      if (!api.url) {
        return
      }

      if (api.body && api.body.filterTerms) {
        if (api.body.filterTerms.filter(filterTerm =>
          L.isString(filterTerm.value) || L.isNumber(filterTerm.value) || L.isArray(filterTerm.values)).length !== api.body.filterTerms.length) {
          return
        }
      }

      try {
        this.startLoader({ id: this.fieldConfig.id })
        const response = await this.$axios({
          ...api.config,
          method: api.method,
          url: api.url,
          data: api.body
        })

        const _items = api.path ? L.get(response, api.path, []) : response.data

        this._items = _items
        this.items = this.processAndSortItems(_items)
        this.$emit('itemsLoaded', _items)
      } catch (error) {
        this.notifyError(error)
      } finally {
        this.stopLoader({ id: this.fieldConfig.id })
      }
    },

    processItems (_items) {
      let items = _items.map(i => {
        // When items are array of strings, convert each items into an object for correct handling
        // by FomrSelect stack
        if (typeof i === 'string') {
          return {
            [this.fieldConfig.itemText]: i,
            [this.fieldConfig.itemValue]: i
          }
        }
        return i
      })
      // /////////////////////////////////////////////////////////////////////////////////////////

      // When field config defines an "itemTextExpr", resolve the experession and add the field to the item
      if (this.fieldConfig.itemTextExpr) {
        items.forEach((item) => {
          const transformJson = {}
          transformJson[this.fieldConfig.itemText] = this.fieldConfig.itemTextExpr
          Configuration.datatable.jsonTransform(item, transformJson, item)
        })
      }
      // ///////////////////////////////////////////////////////////////////////////////////////

      if (this.fieldConfig.filter && typeof this.fieldConfig.filter === 'function') {
        items = items.filter(this.fieldConfig.filter)
      }

      return items
    },

    processAndSortItems (_items) {
      const items = this.processItems(_items)

      const sortField = this.fieldConfig.sortField ? this.fieldConfig.sortField : this.fieldConfig.itemText
      items.sort(this.propComparator(sortField))
      return items
    },

    forceProcessItems () {
      this.items = this.processAndSortItems(this._items)
    },

    propComparator (prop) {
      return function (a, b) {
        let result = 0
        result = a[prop] < b[prop] ? -1 : result
        result = a[prop] > b[prop] ? 1 : result
        return result
      }
    },

    getFormSelectWorkspaceData (colConfig, trackerDTO) {
      // this method specified in colConfig and called by mounted to initialize select list
      const applicationId = trackerDTO.applicationId ? trackerDTO.applicationId : this.applicationId
      const workspacesObject = Configuration.auth.getNestedWorkspaces(
        applicationId, Configuration.getApplication(applicationId).workspaces, {})
      // each key in workspacesObject has the following object as value
      // description: ""
      // id: "sorintlab.candidates.italy"
      // name: "people.label.workspaceItaly"
      // trackers: Array(3)
      // users: Object
      this.items = this.processAndSortItems(Object.keys(workspacesObject).map((key) => {
        return {
          id: workspacesObject[key].id,
          label: this.$t(workspacesObject[key].name)
        }
      }))
    },

    getFormSelectCollaboratorsData (colConfig, trackerDTO) {
      // this method specified in colConfig and called by mounted to initialize select list
      // uses collaborator api to retrieve a tracker collaborators
      // e.g. /api/$applicationId/tracker/$trackerTypeId/$trackerId/collaborator
      if (trackerDTO.id) {
        this.startLoader({ id: colConfig.id })

        const url = Vue.axios.agilisUrlTrackerCollaborators(
          trackerDTO.applicationId,
          trackerDTO.trackerTypeId,
          trackerDTO.id)
        Vue.axios
          .get(url,
            Vue.axios.agilisRequestConfig(url))
          .then((response) => {
            // response.data is an array of collaborator dtos
            let valueFound
            this.items = this.processAndSortItems(response.data.map(collaboratorDTO => {
              if (this.value.id === collaboratorDTO.trackerUser.id) {
                // find selected collaborator
                valueFound = this.value
              }
              return collaboratorDTO.trackerUser
            }))

            if (this.value.id && !valueFound) {
              // selected user is not a collaborator
              this.notifyMessage(this.$t('error.collaboratorNotFound',
                { firstName: this.value.firstName, lastName: this.value.lastName }))
            }
          })
          .catch((error) => {
            this.notifyError(error)
          })
          .finally(() => {
            this.stopLoader({ id: colConfig.id })
          })
      }
    },

    getFormSelectParentIdData: L.debounce(async function (colConfig, trackerDTO, value) {
      this.handleSelectClear()
      // this method specified in colConfig and called by mounted to initialize select list
      const applicationId = trackerDTO.applicationId ? trackerDTO.applicationId : this.applicationId
      const trackerTypeId = this.trackerTypeId
      const trackerId = this.trackerId

      let query = value

      let firstCall = false
      if (query === undefined && !this.isEmpty) {
        query = `${this.value}`
        firstCall = true
      }

      if (query && query.trim()) {
        this.startLoader({ id: colConfig.id })
        const url = Vue.axios.agilisUrlTrackerParentsSearch(
          applicationId,
          trackerTypeId,
          trackerId
        )
        try {
          const response = await Vue.axios.get(
            url,
            {
              ...Vue.axios.agilisRequestConfig(url),
              params: {
                search: query.trim()
              }
            }
          )
          this.items = this.processAndSortItems(response.data)
          if (!this.items.length && trackerDTO[colConfig.id] && firstCall) {
            this.notifyMessage(this.$t('error.parentNotFound',
              { id: query }))
            this.handleSelectChange({ [colConfig.itemValue]: {} })
          }
        } catch (error) {
          this.notifyError(error)
        } finally {
          this.stopLoader({ id: colConfig.id })
        }
      }
    }, 200),

    handleSearchInputChange (value) {
      if (this.fieldConfig.method) {
        const method = this.getMethod()
        method && method(this.fieldConfig, this.trackerDTO, value)
      }
    },

    handleSelectClear () {
      this.items = []
    },

    setConfigDefaults () {
      // default item-text and item-value
      this.fieldConfig.itemText = this.fieldConfig.itemText ? this.fieldConfig.itemText : 'text'
      this.fieldConfig.itemValue = this.fieldConfig.itemValue ? this.fieldConfig.itemValue : 'id'
      // default output value
      this.fieldConfig.outValue = this.fieldConfig.outValue ? this.fieldConfig.outValue : this.fieldConfig.itemValue
    },
    handleAddEntity () {
      this.customItemDTO['applicationId'] = this.fieldConfig.actionCreate.applicationId || this.applicationId
      this.customItemDTO['workspaceId'] = this.fieldConfig.actionCreate.workspaceId || this.workspaceId
      this.customItemDTO['trackerTypeId'] = this.fieldConfig.actionCreate.trackerTypeId || this.trackerTypeId
      this.apiFromConfig = this.fieldConfig.actionCreate.api?.url
      this.openDialog = true
    }
  }
}
